/**
 * React Query hooks for Expenses Management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expensesApi, categoriesApi, suppliersApi, supplierInvoicesApi, recurringExpensesApi, supplierCategoriesApi } from '../lib/api';

// ========== QUERY KEYS ==========
export const expenseKeys = {
  all: ['expenses'],
  general: (filters) => [...expenseKeys.all, 'general', filters],
  stats: (filters) => [...expenseKeys.all, 'stats', filters],
  categories: () => [...expenseKeys.all, 'categories'],
  suppliers: (filters) => [...expenseKeys.all, 'suppliers', filters],
  invoices: (filters) => [...expenseKeys.all, 'invoices', filters],
  invoiceStats: (filters) => [...expenseKeys.all, 'invoiceStats', filters],
  recurring: () => [...expenseKeys.all, 'recurring'],
};

// ========== GENERAL EXPENSES ==========
export function useGeneralExpenses(filters = {}) {
  return useQuery({
    queryKey: expenseKeys.general(filters),
    queryFn: () => expensesApi.getAll(filters),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

export function useGeneralExpenseStats(filters = {}) {
  return useQuery({
    queryKey: expenseKeys.stats(filters),
    queryFn: () => expensesApi.getStats(filters),
    staleTime: 1 * 60 * 1000,
  });
}

export function useCreateGeneralExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: expensesApi.create,
    onSuccess: async () => {
      // Invalidate all expense queries then force refetch
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    },
  });
}

export function useUpdateGeneralExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => expensesApi.update(id, data),
    onSuccess: async () => {
      // Reset all expense queries to force fresh data (clears cache completely)
      await queryClient.resetQueries({ queryKey: expenseKeys.all });
    },
  });
}

export function useDeleteGeneralExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: expensesApi.delete,
    onSuccess: async () => {
      // Invalidate all expense queries then force refetch
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    },
  });
}

export function useMarkGeneralExpensePaid() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      return fetch(`${API_BASE}/api/general-expenses/${id}/mark-paid`, { method: 'PATCH' })
        .then(res => res.json());
    },
    onSuccess: async () => {
      // Invalidate all expense queries then force refetch
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    },
  });
}

// ========== CATEGORIES ==========
export function useExpenseCategories() {
  return useQuery({
    queryKey: expenseKeys.categories(),
    queryFn: categoriesApi.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutes (categories don't change often)
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: categoriesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.categories() });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => categoriesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.categories() });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: categoriesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.categories() });
    },
  });
}

// ========== SUPPLIER CATEGORIES ==========
export const supplierCategoryKeys = {
  all: ['supplier-categories'],
  list: () => [...supplierCategoryKeys.all, 'list'],
};

export function useSupplierCategories() {
  return useQuery({
    queryKey: supplierCategoryKeys.list(),
    queryFn: supplierCategoriesApi.getAll,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateSupplierCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: supplierCategoriesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierCategoryKeys.all });
    },
  });
}

export function useUpdateSupplierCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => supplierCategoriesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierCategoryKeys.all });
    },
  });
}

export function useDeleteSupplierCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: supplierCategoriesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierCategoryKeys.all });
    },
  });
}

// ========== SUPPLIERS ==========
export function useSuppliers(filters = {}) {
  return useQuery({
    queryKey: expenseKeys.suppliers(filters),
    queryFn: () => suppliersApi.getAll(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: suppliersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.suppliers() });
    },
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => suppliersApi.update(id, data),
    onSuccess: () => {
      // Only invalidate suppliers query - not all expenses
      queryClient.invalidateQueries({ queryKey: expenseKeys.suppliers() });
    },
  });
}

export function useDeleteSupplier() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: suppliersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.suppliers() });
    },
  });
}

export function useDeleteSuppliers() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (ids) => suppliersApi.deleteMany(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.suppliers() });
    },
  });
}

// ========== SUPPLIER INVOICES ==========
export function useSupplierInvoices(filters = {}) {
  return useQuery({
    queryKey: expenseKeys.invoices(filters),
    queryFn: () => supplierInvoicesApi.getAll(filters),
    staleTime: 1 * 60 * 1000,
  });
}

export function useSupplierInvoiceStats(filters = {}) {
  return useQuery({
    queryKey: expenseKeys.invoiceStats(filters),
    queryFn: () => supplierInvoicesApi.getStats(filters),
    staleTime: 1 * 60 * 1000,
  });
}

export function useCreateSupplierInvoice() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: supplierInvoicesApi.create,
    onSuccess: async () => {
      // Invalidate all expense queries then force refetch
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    },
  });
}

export function useUpdateSupplierInvoice() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => supplierInvoicesApi.update(id, data),
    onSuccess: async () => {
      // Invalidate all expense queries then force refetch
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    },
  });
}

export function useDeleteSupplierInvoice() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: supplierInvoicesApi.delete,
    onSuccess: async () => {
      // Invalidate all expense queries then force refetch
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    },
  });
}

export function useDeleteSupplierInvoices() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (ids) => supplierInvoicesApi.deleteMany(ids),
    onSuccess: async () => {
      // Invalidate all expense queries then force refetch
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    },
  });
}

export function useMarkInvoicePaid() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: supplierInvoicesApi.markPaid,
    onSuccess: async () => {
      // Invalidate all expense queries then force refetch
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    },
  });
}

export function useAddInvoicePayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ invoiceId, data }) => supplierInvoicesApi.addPayment(invoiceId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    },
  });
}

export function useCreateDebitNote() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: supplierInvoicesApi.createDebitNote,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    },
  });
}

// ========== RECURRING EXPENSES ==========
export function useRecurringExpenses() {
  return useQuery({
    queryKey: expenseKeys.recurring(),
    queryFn: recurringExpensesApi.getAll,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateRecurringExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: recurringExpensesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.recurring() });
    },
  });
}

export function useUpdateRecurringExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => recurringExpensesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.recurring() });
    },
  });
}

export function useDeleteRecurringExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: recurringExpensesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.recurring() });
    },
  });
}

export function useToggleRecurringExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: recurringExpensesApi.toggle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.recurring() });
    },
  });
}

export function useGenerateRecurringExpenses() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: recurringExpensesApi.generate,
    onSuccess: async () => {
      // Invalidate all expense queries then force refetch
      await queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      queryClient.refetchQueries({ queryKey: expenseKeys.all, type: 'active' });
    },
  });
}

